// Import React dependencies.
import { debounce } from 'lodash';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
// Import the Slate editor factory.
import { BaseEditor, Descendant, Editor, Transforms } from 'slate';
// Import the Slate components and React plugin.
import { ReactEditor } from 'slate-react';
import { Slate } from 'slate-react';

import theme from '../../assets/theme';
import { newRelicNoticeError } from '../../hooks/useNewRelic';
import { isCuripodPastedSlide } from '../../utils/copyAndPasteSlidesUtils';
import { toggleMark } from './richTextEditorUtil';
import {
  CuripodDecendant,
  CuripodElement,
  EditorInitialState,
  Element,
  handleSlateKeyPress,
  isCuripodText,
  isValidUrl,
  Leaf,
  parseStringToSlateObject,
  RenderElementProps,
  SlateEditable,
  SlateWrapper,
} from './slateUtil';
import { SlideElement } from './slide.types';

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & { id: string; content: CuripodElement[] };
    Element: CuripodElement;
    Decendant: CuripodDecendant;
  }
  interface BaseText {
    link?: string;
    color?: string;
  }
}

export type RichTextEditorType = BaseEditor &
  ReactEditor & {
    id: string;
    content: CuripodElement[];
    isFocused?: boolean;
  };

export const INITIAL_EMPTY_SLATE_STATE = {
  align: 'center',
  color: theme.colors.black,
  size: 16,
};
interface ErrorBoundaryProps {
  children: React.ReactNode;
  richTextEditor: RichTextEditorType; // Assuming this is the Slate editor instance
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
  private richTextEditor: RichTextEditorType;
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.richTextEditor = props.richTextEditor;
  }

  componentDidCatch(error: Error, _: React.ErrorInfo) {
    // You could log the error or perform other actions here.

    const { selection } = this.props.richTextEditor;

    if (!error.message.includes('Cannot resolve a DOM point from Slate point')) {
      newRelicNoticeError(error, 'Error in SlateRichTextEditor, ErrorBoundary');
    }
    if (selection) {
      Transforms.deselect(this.props.richTextEditor);
    }
  }

  render() {
    return this.props.children;
  }
}
const SlateRichTextEditor = ({
  onChange,
  disabledButtons,
  disabledKeys,
  setSelectedElements,
  onDelete,
  richTextEditor,
  initContent,
  autoFocus,
}: {
  onChange: (title: string) => void;
  disabledButtons?: string[];
  disabledKeys?: string[];
  initContent: string;
  onDelete?: () => void;
  initialEmptyState?: EditorInitialState;
  setSelectedElements?: (element: SlideElement | null) => void;
  richTextEditor: RichTextEditorType;
  autoFocus: boolean;
}) => {
  const editorRef = useRef<Editor | null>();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [content, setContent] = useState<CuripodElement[] | undefined>();
  // Make sure only one slate rich text editor has focus
  // clears content when switching between slides and needs to render a new editor at the same place
  useLayoutEffect(() => {
    if (!editorRef.current || richTextEditor.id !== editorRef.current.id) {
      const newContent: CuripodElement[] | undefined = parseStringToSlateObject(
        initContent,
      );
      setContent(newContent);
      editorRef.current = richTextEditor;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [richTextEditor, initContent]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback(
    debounce((value: Descendant[]) => {
      const change = JSON.stringify(value.filter(e => !!e));
      if (change !== JSON.stringify(content)) {
        onChange(change);
      }
    }, 20),
    [content, onChange],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      // Prevent accidentally pasting curipod slide pasted payload into inputs
      const pastedText = e.clipboardData.getData('text');
      if (isCuripodPastedSlide(pastedText)) {
        e.preventDefault();
      }

      if (isValidUrl(pastedText)) {
        const { selection } = richTextEditor;

        if (selection) {
          const selectedText = Editor.string(richTextEditor, selection);
          const marks = Editor.marks(richTextEditor);

          if (selectedText && selectedText.length > 0) {
            e.preventDefault();
          }
          if (marks && !marks.link) {
            toggleMark(richTextEditor, 'link', pastedText);
          }
        }
      }
    },
    [richTextEditor],
  );

  if (!editorRef.current) return null;
  if (editorRef.current.id !== richTextEditor.id) return null;
  if (!content) return null;
  return (
    <SlateWrapper>
      <span translate="no">
        <Slate
          key={richTextEditor.id}
          editor={richTextEditor}
          value={content}
          onChange={handleChange}
        >
          <ErrorBoundary richTextEditor={richTextEditor}>
            <SlateEditable
              autoFocus={autoFocus}
              className={richTextEditor.id}
              id={richTextEditor.id}
              readOnly={false}
              style={{ cursor: isFocused ? 'text' : 'pointer' }}
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={e => {
                e.preventDefault();
                e.stopPropagation();
                // prevent onBlur when clicking on the emoji action
                if (
                  e.relatedTarget?.className &&
                  e.relatedTarget?.className.includes('epr-emoji')
                )
                  return;
              }}
              placeholder={'Enter text...'}
              onPaste={handlePaste}
              $readOnly={false}
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (disabledKeys?.includes(event.key)) {
                  event.preventDefault();
                  return;
                }
                // since we dont have history (note: we can't just use the regular withHistory) inside slate we deselect the slate element and let the regular undo/redo handle it
                // this is a workaround for now
                if (setSelectedElements && event.key === 'z' && event.metaKey) {
                  event.preventDefault();
                  setSelectedElements(null);
                  return;
                }
                if (setSelectedElements && event.key === 'Escape') {
                  Transforms.deselect(richTextEditor);
                  setSelectedElements(null);
                  return;
                }
                if (editorRef.current) {
                  handleSlateKeyPress({
                    event,
                    editor: editorRef.current,
                    disabledButtons,
                    onDelete,
                  });
                }
              }}
              renderElement={(props: RenderElementProps) => (
                <Element {...props} className={richTextEditor.id} />
              )}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              renderLeaf={(props: any) => <Leaf {...props} />}
            ></SlateEditable>
          </ErrorBoundary>
        </Slate>
      </span>
    </SlateWrapper>
  );
};

export default SlateRichTextEditor;
